/**
 * Choosing a product's part family.
 *
 * The one field on a hand-created product that an operator cannot invent, and the
 * one with the largest blast radius if it is missing. The B2B's cross-reference
 * call is "search by comparable number, limited to this article's
 * `genericArticleIds`" — read off the article we inject. A product with no family
 * publishes an empty list, the B2B sends an empty filter, and TecDoc answers with
 * every part in the catalogue that carries that number in any category: a BCD pump
 * came back with 76 articles from unrelated brands.
 *
 * So the id has to be TecDoc's real one. Every article we have ever cached names
 * its own, which is what this searches — a picker over observed reality rather
 * than a taxonomy somebody has to maintain.
 */
import { useEffect, useState } from 'react';
import { useApi } from '../lib/api';

interface Family {
  id: number;
  description: string | null;
  assembly_group: string | null;
  seen: number;
}

export default function FamilyPicker({
  value,
  onPick,
}: {
  /** The id currently stored in `attrs.generic_article`, if any. */
  value: number | null;
  onPick: (family: { id: number; description: string } | null) => void;
}) {
  const { request } = useApi();
  const [q, setQ] = useState('');
  const [items, setItems] = useState<Family[]>([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<Family | null>(null);

  // Resolve whatever is already stored, so the panel shows a name rather than a
  // bare number the operator would have to look up somewhere else.
  useEffect(() => {
    if (value === null) {
      setCurrent(null);
      return;
    }
    let live = true;
    void (async () => {
      const r = await request<{ items: Family[] }>('GET', `/v1/generic-articles?q=${value}`);
      if (!live) return;
      setCurrent(r.data?.items?.find((f) => f.id === value) ?? { id: value, description: null, assembly_group: null, seen: 0 });
    })();
    return () => {
      live = false;
    };
  }, [value, request]);

  useEffect(() => {
    if (!open) return;
    let live = true;
    const t = setTimeout(async () => {
      const r = await request<{ items: Family[] }>('GET', `/v1/generic-articles?q=${encodeURIComponent(q)}&limit=40`);
      if (live) setItems(r.data?.items ?? []);
    }, 200);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [q, open, request]);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        {current ? (
          <span className="chip mono">
            {current.id}
            {current.description ? ` · ${current.description}` : ''}
          </span>
        ) : (
          <span className="text-[12px] text-warn">
            No part family — the B2B cannot ask for this product’s cross references
          </span>
        )}
        <button className="btn btn-sm" onClick={() => setOpen((v) => !v)}>
          {open ? 'Close' : current ? 'Change' : 'Choose'}
        </button>
        {current && (
          <button className="btn btn-sm" onClick={() => onPick(null)}>
            Clear
          </button>
        )}
      </div>

      {open && (
        <div className="rounded border border-line bg-panel2 p-2">
          <input
            className="input"
            autoFocus
            placeholder="Search: pastiglie, pompa, batteria, or a numeric id…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="mt-2 max-h-56 overflow-auto">
            {items.length === 0 ? (
              <div className="p-2 text-[12px] text-faint">
                {q ? 'Nothing matches — the catalogue has to have been seen at least once.' : 'Type to search.'}
              </div>
            ) : (
              items.map((f) => (
                <button
                  key={f.id}
                  className="flex w-full items-baseline gap-2 rounded px-2 py-1 text-left text-[12.5px] hover:bg-panel"
                  onClick={() => {
                    onPick({ id: f.id, description: f.description ?? '' });
                    setOpen(false);
                  }}
                >
                  <span className="mono text-muted" style={{ minWidth: 52 }}>
                    {f.id}
                  </span>
                  <span>{f.description ?? '—'}</span>
                  <span className="ml-auto text-[11px] text-faint">
                    {f.assembly_group ?? ''} · {f.seen}×
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
